import * as yup from 'yup'
import { MakeRequiredAndNullable } from './utils.js'

export enum IHPOLineItemKeys {
  itemNumber = 'itemNumber', // line item number, not to be entered by user
  itemName = 'itemName', // name of the item, often SKU, Unique ID, reference ID, etc
  name = 'name', // descriptive name of the item
  description = 'description', // verbose description of the item (human parseable)
  shipDate = 'shipDate', // per-line ship date, optional
  deliveryDate = 'deliveryDate', // per-line expected delivery date, optional
  itemQty = 'itemQty', // quantity of the item for order
  unitPrice = 'unitPrice', // price per unit
  discount = 'discount', // per-line discount amount, optional
  taxes = 'taxes', // per-line tax amount, optional
  shippingCost = 'shippingCost', // per-line shipping cost, optional
  lineTotal = 'lineTotal', // total cost per (unitPrice * itemQty), not to be entered by user
}

export const IHPOAddressInfoSchema = yup.object().shape({
  attn: yup.string().nullable().optional(),
  companyName: yup.string().nullable().optional(),
  addressLine1: yup.string().nullable().optional(),
  addressLine2: yup.string().nullable().optional(),
  city: yup.string().nullable().optional(),
  state: yup.string().nullable().optional(),
  zipCode: yup.string().nullable().optional(),
  country: yup.string().nullable().optional(),
  phoneNumber: yup.string().nullable().optional(),
  emailAddress: yup.string().nullable().optional(),
  displayMultipleLines: yup.string().nullable().optional(),
})
export type IHPOFileAddressData = yup.InferType<typeof IHPOAddressInfoSchema>

const ihLineItemSchema = yup.object().shape({
  itemNumber: yup.number().nullable().optional(),
  itemName: yup.string().nullable().optional(),
  name: yup.string().nullable().optional(),
  description: yup.string().nullable().optional(),
  shipDate: yup.string().nullable().optional(),
  deliveryDate: yup.string().nullable().optional(),
  itemQty: yup.string().nullable().optional(),
  unitPrice: yup.string().nullable().optional(),
  discount: yup.string().nullable().optional(),
  taxes: yup.string().nullable().optional(),
  shippingCost: yup.string().nullable().optional(),
  lineTotal: yup.string().nullable().optional(),
})

export const IHPOFileDataSchema = yup.object().shape({
  issuer: IHPOAddressInfoSchema,
  orderNumber: yup.string().nullable().optional(),
  dateIssued: yup.string().nullable().optional(),
  expectedDeliveryDate: yup.string().nullable().optional(),
  dateReady: yup.string().nullable().optional(),
  paymentTerms: yup.string().nullable().optional(),
  invoiceTerms: yup.string().nullable().optional(),
  freightTerms: yup.string().nullable().optional(),
  currency: yup.string().nullable().optional(),
  destination: IHPOAddressInfoSchema,
  vendor: IHPOAddressInfoSchema,
  items: yup.array().of(ihLineItemSchema).required(),
  items2: yup.array().of(ihLineItemSchema).optional(),
  dontRoundUnitPrice: yup.string().nullable().optional(),
  agreement: yup.string().nullable().optional(),
  instructions: yup.string().nullable().optional(),
  termsAndConditionsText: yup.string().nullable().optional(),
  useLocaleQuantityFormatting: yup.string().nullable().optional(),
  termsAndConditionsUrl: yup.string().nullable().optional(),
  termsAndConditionsLabel: yup.string().nullable().optional(),
  taxes: yup.string().nullable().optional(),
  shippingCost: yup.string().nullable().optional(),
})

export type IHPOFileData = yup.InferType<typeof IHPOFileDataSchema>
type RequiredFileData = MakeRequiredAndNullable<IHPOFileData>

export const IHPOFileMappings: RequiredFileData = {
  issuer: {
    attn: '1ee23a9d-6948-4335-887c-91b8aa177199',
    companyName: '70e0a966-effc-4cda-9d58-8b1901baab58',
    addressLine1: 'a50ea5f9-835c-4bf1-a0ec-f2996db63258',
    addressLine2: '485acbb1-2035-450c-995d-84b2076c7f90',
    city: '86370e90-40f9-4214-9a1a-d51524e74e86',
    state: '1be1114b-71ae-4139-a13a-4f3fffdf6139',
    zipCode: 'e4e68d0e-a901-4c6d-ae9d-fe6ca38fd0e8',
    country: '01fe72a7-798d-482f-ad86-62fe6ac2f62d',
    phoneNumber: '8c14d3c7-118a-47c4-bde4-aa39df2c73b3',
    emailAddress: 'd20787be-0ec7-42e6-9247-681db9d5f9c2',
    displayMultipleLines: '56e43e5b-fc5f-4aa7-a97d-23d4ac5c71d3',
  },
  orderNumber: '806b0846-7209-476b-ac1f-ebe512d7acc8',
  dateIssued: '29886b98-81ff-4cad-a530-cd6c732404ac',
  expectedDeliveryDate: '7efe18fc-e585-4c5c-8001-d07f7e821562',
  dateReady: '4d8e37ef-9021-4926-ae92-0158c15b5528',
  paymentTerms: '36493d94-fe4b-4d48-8b7d-8a379cc5d319',
  invoiceTerms: 'f144bb9c-59a9-473f-989d-c98609655d90',
  freightTerms: '0c57151c-a3bc-4c91-af0d-e2a99e4bb16f',
  currency: '9f246d57-45a7-4b43-b4ef-3e56420a6fff',
  taxes: '5c5544c5-6fd4-47c0-bbeb-6b9043fb5136',
  shippingCost: '2bcef1e7-9202-4186-9bbe-23cf930b5673',
  destination: {
    attn: '1dcbb563-f9e6-4d1f-b659-1aaf1f97ca95',
    companyName: '99d2317d-5002-4a81-8f7f-199b34e188eb',
    addressLine1: '1a758c5f-48a3-4810-9d0b-a0a1ddf86e7e',
    addressLine2: '6b275374-f686-4521-b949-c5afebbff090',
    city: '0758892d-d609-4a71-a445-d2e934f9220d',
    state: '61419d14-6312-4a04-b1c9-7007734d92f8',
    zipCode: 'ff1b9ede-ca79-4a25-9d2b-93ca858cfcea',
    country: '5484a2b1-d30e-4f5a-998c-1ca589af5fcd',
    phoneNumber: 'db09d0e5-5bb7-40c9-99a8-3928ef320407',
    emailAddress: 'e10ecd93-235d-4035-a6a7-bf671330d4f3',
    displayMultipleLines: 'b24ddc9d-3c4a-4150-a2b5-cfcc389dcaac',
  },
  vendor: {
    attn: 'a5061d20-6c41-446a-baf7-1d46090a8972',
    companyName: '596bb3ec-9a28-46a3-948f-b8abc4c57cab',
    addressLine1: 'c58e9fe4-dc82-4a75-bb0d-e58d77526620',
    addressLine2: 'f7c5472a-2ff1-42ec-9370-e44aac6826c8',
    city: '81c776f7-d434-4356-a4bd-a8eeae746dfe',
    state: 'd59a66ba-70d3-47ea-827c-53e62f2f552c',
    zipCode: '553582cf-8a3c-4acc-adf4-1f8ef4be3cee',
    country: '66565b84-27b7-4641-826c-8515b6d5e024',
    phoneNumber: 'b3f93aea-e4b6-4696-911a-e6ea585ccac7',
    emailAddress: 'ce6c4216-59c4-471a-b1d8-7f58a79c1c9c',
    displayMultipleLines: 'edd697ad-c72b-4303-8072-e4c8af017489',
  },
  items: [
    {
      itemNumber: null,
      itemName: '5afc1179-740c-4132-9505-e58c237c0118',
      name: '85704f93-36d8-4c4c-9e41-b1a4175ae54b',
      description: 'dd197c50-eab5-4c70-93b7-0938dd9db430',
      shipDate: '6c7ee6b6-75cf-4aa7-abd8-e95a4554e507',
      deliveryDate: 'bc2cf4e2-2f2f-4d41-ab56-320f3b4692ad',
      itemQty: 'abdb0566-9efb-471a-b758-8b65f4ee5784',
      unitPrice: 'aef3b54e-54f3-440d-99af-be3d8a26288c',
      discount: '96032856-5a8d-4e38-9d0d-25f10c693315',
      taxes: '2d7d1cf3-ed15-4ea2-b5d9-502335961206',
      shippingCost: 'e167dfdd-20b8-43ad-ad53-2374d560fb22',
      lineTotal: 'cfd91abd-56a7-418d-b594-511a78f9fe9b',
    },
  ],
  items2: [
    {
      itemNumber: null,
      itemName: '79b4ecea-2b29-4180-b7fd-a2f8c9ff0132',
      name: 'e1139585-82ba-440b-8c3a-b3ac49cbb479',
      description: '13389245-cc9b-4900-88db-4ed62979dd51',
      shipDate: '4e8b77bb-917a-4fe5-9e82-8455a832fbc1',
      deliveryDate: '70c3759a-f013-4955-830f-f21835f23ddc',
      itemQty: 'c6cb2272-a763-40a7-b6d8-1a78a9b1d163',
      unitPrice: '711104e0-e7f2-418d-b22e-df6d2d0f63ae',
      discount: '55ec2c61-54cd-47ca-8c23-b52b46251ffe',
      taxes: '5682c2c2-527b-4610-8621-0c86fa1f36e0',
      shippingCost: 'e3116512-543e-43d3-9844-836e6634bcf6',
      lineTotal: '1b5f8696-a29c-4c03-837e-30a4e5abbe4a',
    },
  ],
  dontRoundUnitPrice: '2615efe3-9dd4-45b0-9508-264fd03c504a',
  agreement: '213742c6-e4e2-4b9e-8569-4d4a51d86575',
  instructions: '19365f7f-1810-4500-b1d5-337ce16deb8d',
  termsAndConditionsText: '71cfbd9a-3520-4b55-8509-1fb4dcdcb669',
  useLocaleQuantityFormatting: 'e0e98949-9e97-40fd-bcc6-c63c95c54900',
  termsAndConditionsUrl: '9722fcb2-bf7d-447e-b59e-82490578c809',
  termsAndConditionsLabel: '7f0213f5-1577-4e94-81dd-0da2fb8cb013',
}
